import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { drawBodhiLeaf } from './shapes';
import { recolorGradient } from './recolor';
import lotusGradient from '@shared/assets/falling-petals/lotus-gradient.png';

// Canvas 座標系以中心點為軸心，shapes.js 的繪圖函式是以 size x size 的方框（原點在左上）為準，
// 這裡用 translate 把座標系移到方框中心，讓外部呼叫時可以直接以物件中心點旋轉/定位。
function drawCentered(drawFn) {
    return (ctx, size, color) => {
        ctx.save();
        ctx.translate(-size / 2, -size / 2);
        drawFn(ctx, size, color);
        ctx.restore();
    };
}

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

/**
 * 飄落蓮花 / 菩提葉特效 - Canvas 2D 粒子系統
 * Falling lotus flowers / bodhi leaves - Canvas 2D particle system
 *
 * 蓮花使用成熟美術素材（B 素材，側視漸層彩色，取自 freesvg.org CC0），
 * 菩提葉維持程式繪製。使用 requestAnimationFrame 驅動，模擬風向擺動。
 * 蓮花與菩提葉飄落時都會持續翻轉，像真實花瓣/落葉打轉飄落。
 */
const FallingPetalsCanvas = ({
    type = 'lotus',
    count = 40,
    color = '#F5C97B',
    minSize = 14,
    maxSize = 30,
    wind = 0.3,
    speed = 1,
    className = '',
}) => {
    const canvasRef = useRef(null);
    const particlesRef = useRef([]);
    const rafRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let width = 0;
        let height = 0;
        let dpr = Math.min(window.devicePixelRatio || 1, 2);
        let cancelled = false;
        let draw = null;

        const resize = () => {
            const parent = canvas.parentElement;
            width = parent.clientWidth;
            height = parent.clientHeight;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const createParticle = (initial = false) => ({
            x: randomBetween(0, width),
            y: initial ? randomBetween(-height, height) : -30,
            size: randomBetween(minSize, maxSize),
            fallSpeed: randomBetween(0.4, 1.1) * speed,
            swayAmplitude: randomBetween(15, 45),
            swaySpeed: randomBetween(0.4, 1.2),
            swayOffset: randomBetween(0, Math.PI * 2),
            rotation: randomBetween(0, Math.PI * 2),
            rotationSpeed: randomBetween(-0.02, 0.02),
            opacity: randomBetween(0.55, 0.95),
        });

        const init = () => {
            resize();
            particlesRef.current = Array.from({ length: count }, () => createParticle(true));
        };

        let elapsed = 0;
        const render = () => {
            if (!draw) {
                rafRef.current = requestAnimationFrame(render);
                return;
            }
            elapsed += 1;
            ctx.clearRect(0, 0, width, height);

            for (const p of particlesRef.current) {
                p.y += p.fallSpeed;
                p.x += Math.sin(elapsed * 0.01 * p.swaySpeed + p.swayOffset) * 0.6 + wind;
                p.rotation += p.rotationSpeed;

                if (p.y > height + 30) {
                    Object.assign(p, createParticle(false));
                }
                if (p.x > width + 30) p.x = -30;
                if (p.x < -30) p.x = width + 30;

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);
                ctx.globalAlpha = p.opacity;
                draw(ctx, p.size, color);
                ctx.restore();
            }

            rafRef.current = requestAnimationFrame(render);
        };

        if (type === 'bodhi') {
            draw = drawCentered(drawBodhiLeaf);
            init();
            rafRef.current = requestAnimationFrame(render);
        } else {
            recolorGradient(lotusGradient, color).then((coloredCanvas) => {
                if (cancelled) return;
                draw = (ctx2, size) => {
                    ctx2.drawImage(coloredCanvas, -size / 2, -size / 2, size, size);
                };
                init();
                rafRef.current = requestAnimationFrame(render);
            });
        }

        const handleResize = () => {
            const oldWidth = width;
            resize();
            const scaleX = oldWidth ? width / oldWidth : 1;
            particlesRef.current.forEach((p) => { p.x *= scaleX; });
        };
        window.addEventListener('resize', handleResize);

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', handleResize);
        };
    }, [type, count, color, minSize, maxSize, wind, speed]);

    return (
        <canvas
            ref={canvasRef}
            className={className}
            aria-hidden="true"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 5,
            }}
        />
    );
};

FallingPetalsCanvas.propTypes = {
    type: PropTypes.oneOf(['lotus', 'bodhi']),
    count: PropTypes.number,
    color: PropTypes.string,
    minSize: PropTypes.number,
    maxSize: PropTypes.number,
    wind: PropTypes.number,
    speed: PropTypes.number,
    className: PropTypes.string,
};

export default FallingPetalsCanvas;
